import * as ms from 'mousetrap';
import { DependencyList, useEffect, useMemo } from 'react';
import { atom, useRecoilState } from 'recoil';
import { useAuthState } from 'state/useAuthState';
import { usePostHog } from 'posthog-js/react';
import { isProductionEnvironment } from 'shared/envUtils';
import { KeyboardShortcut } from './useKeyboardShortcut.types';

const registeredShortcutStatAtom = atom<KeyboardShortcut[]>({
  key: 'REGISTERED_SHORTCUTS',
  default: [],
});

export const useRegisteredShortcuts = (): KeyboardShortcut[] => {
  const [registeredShortcuts] = useRecoilState(registeredShortcutStatAtom);
  return [...registeredShortcuts];
};

export const useKeyboardShortcuts = (
  shortcuts: KeyboardShortcut[],
  deps: DependencyList = [],
): void => {
  const {
    permissions: { isInternalUser },
  } = useAuthState();
  const posthog = usePostHog();
  const [, setRegisteredShortcuts] = useRecoilState(registeredShortcutStatAtom);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedShortcuts = useMemo(() => shortcuts, deps);

  useEffect(() => {
    if (
      (isProductionEnvironment() && !isInternalUser) ||
      memoizedShortcuts.length === 0
    ) {
      return () => {};
    }

    setRegisteredShortcuts((prevRegistry) => [
      ...prevRegistry,
      ...memoizedShortcuts,
    ]);

    memoizedShortcuts.forEach(({ keys, description, handler }) => {
      ms.bind(keys, (...args) => {
        posthog.capture('keyboardShortcut', { description });
        handler(...args);
      });
    });

    return () => {
      setRegisteredShortcuts((prevRegistry) =>
        prevRegistry.filter(
          (shortcut) =>
            !memoizedShortcuts.some(
              (s) =>
                s.keys === shortcut.keys &&
                s.description === shortcut.description,
            ),
        ),
      );

      memoizedShortcuts.forEach(({ keys }) => {
        ms.unbind(keys);
      });
    };
  }, [memoizedShortcuts, isInternalUser, setRegisteredShortcuts, posthog]);
};
